// @flow
import React from 'react';
import { defineMessages, injectIntl, type IntlShape } from 'react-intl';

import Container from 'brastrap/common/container/Container';
import Rating from 'brastrap/common/rating/Rating';
import Response from 'brastrap/common/response/Response';
import ButtonLink from 'brastrap/common/button-link/ButtonLink';
import Activity from 'brastrap/common/activity/Activity';
import Icon from 'brastrap/common/icon/Icon';

const messages = defineMessages({
  title: {
    id: 'shop-reviews.page-title',
    defaultMessage: 'Latest reviews for our shops',
  },
  readMore: { id: 'shop-reviews.readMore', defaultMessage: 'Read More' },
  button: { id: 'shop-reviews.button', defaultMessage: 'Leave a review' },
  label: {
    id: 'shop-reviews.label',
    defaultMessage: 'Had An Uplifting Experience?',
  },
});

export type Props = {
  intl: IntlShape,
  loading: boolean,
  reviews: Array<CustomerResponse>,
  onReadMoreClick: () => {},
  showReadMore: boolean,
  showWriteReview: boolean,
  rating: {
    total: number,
    value: number,
  },
  hideRatingTotalString: boolean,
};

const CONTAINER_ID = 'shop-reviews';

/**
 * The component to be displayed while the reviews are loading.
 *
 * @constructor
 */
const Loading = () => (
  <div className="c-shop-reviews__loading">
    <Activity />
  </div>
);

/**
 * @param {Object} props
 * @returns {XML}
 * @constructor
 */
const ShopReviews = (props: Props) => {
  const {
    intl: { formatMessage },
    loading,
    reviews,
    rating,
    onReadMoreClick,
    showReadMore,
    showWriteReview,
  } = props;

  const displayReviews = !loading && !!reviews.length;
  const displayRatings = !!rating.total;
  const containerTitle = formatMessage(messages.title, { name: 'Shops' });

  return (
    <Container
      id={CONTAINER_ID}
      modifiers={['ornamented']}
      title={containerTitle}
    >
      <hr className="u-rule" />
      {displayRatings && (
        <div className="c-rating__container">
          <Rating {...rating} hideRatingTotalString={false} size="large" />
        </div>
      )}

      {loading && <Loading />}

      {displayReviews && (
        <div>
          {reviews.map((item, index) => (
            <Response key={index + item.review.date} {...item} />
          ))}
          <div className="c-shop-reviews__read-more">
            {showReadMore && (
              <div
                className="c-shop-reviews__button-text"
                onClick={onReadMoreClick}
              >
                {formatMessage(messages.readMore)}
                <Icon
                  icon="ornament-underline"
                  className="c-shop-reviews__more-icon"
                  size={{ width: 35, height: 29 }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {showWriteReview && (
        <div className="s-prose u-center">
          <h2 className="c-container__title">
            {props.intl.formatMessage(messages.label)}
          </h2>
          <ButtonLink
            modifiers={['filled']}
            url="write-review/"
            rel="nofollow"
            label={props.intl.formatMessage(messages.button)}
          />
        </div>
      )}
    </Container>
  );
};

ShopReviews.defaultProps = {
  reviews: [],
  showReadMore: true,
  showWriteReview: true,
  onReadMoreClick: () => {},
};

export default injectIntl(ShopReviews);
